import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {differenceInCalendarDays, format, formatDistanceToNow} from 'date-fns';
import {ArrowPathIcon, CommandLineIcon, TrashIcon} from '@heroicons/react/24/outline';
import {deleteMcpToken, IMcpToken, rotateMcpToken, TMcpScope, useMcpTokens} from '../../react-sdk/queries';
import {CreateMcpTokenDialog, CreatedMcpTokenDialog, OkCancelDialog} from '../../Dialogs';
import {Button, Spinner} from '../../Components';
import {AppState} from '../../AppState';
import {MCP_SCOPES} from './mcpScopes';

const Card: FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
  <div className={`rounded-lg border border-[#ebecf0] bg-white p-5 ${className}`}>{children}</div>
);

const ICON_BUTTON =
  'rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 disabled:cursor-default disabled:opacity-40 tablet:p-1.5 [&>svg]:h-5 [&>svg]:w-5 tablet:[&>svg]:h-4 tablet:[&>svg]:w-4';

// read is neutral, everyday writes are brand purple, admin scopes are amber like the Global Admin badge, delete is the trash red.
const SCOPE_CHIP: Record<TMcpScope, string> = {
  read: 'bg-gray-100 text-gray-600',
  'envelopes:write': 'bg-[#efedfb] text-[#654dcb]',
  'templates:write': 'bg-[#efedfb] text-[#654dcb]',
  'org:write': 'bg-[#fdf1e3] text-[#95591a]',
  debug: 'bg-[#fdf1e3] text-[#95591a]',
  destructive: 'bg-[#fdebe3] text-[#c2410c]',
};

const ScopeChips: FC<{scopes: TMcpScope[]; globalAdmin: boolean}> = ({scopes, globalAdmin}) => (
  <div className="flex flex-wrap gap-1">
    {globalAdmin && <span className="rounded-full bg-[#fdf1e3] px-2 py-0.5 text-[11px] font-medium text-[#95591a]">Global admin</span>}
    {MCP_SCOPES.filter((s) => scopes.includes(s.value)).map((s) => (
      <span key={s.value} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${SCOPE_CHIP[s.value]}`}>
        {s.label}
      </span>
    ))}
  </div>
);

const Expiry: FC<{at: string | null}> = ({at}) => {
  if (!at) {
    return null;
  }

  const soon = differenceInCalendarDays(new Date(at), new Date()) <= 14;
  return <div className={soon ? 'text-[#95591a]' : 'text-gray-400'}>Expires {format(new Date(at), 'MMM d, yyyy')}</div>;
};

const TokenRow: FC<{token: IMcpToken; onRotate: () => void; onDelete: () => void}> = ({token, onRotate, onDelete}) => (
  <div className="flex flex-col gap-2 border-b border-[#f2f3f5] py-3 last:border-0 tablet:flex-row tablet:items-center tablet:gap-3">
    <div className="min-w-0 tablet:w-[210px] tablet:flex-none">
      <div className="truncate text-[13px] font-medium text-[#33364b]">{token.name}</div>
      <div className="mt-0.5 truncate font-mono text-[11.5px] text-gray-400">{token.token_prefix}…</div>
    </div>

    <div className="flex items-center justify-between gap-3 tablet:contents">
      <div className="min-w-0 tablet:flex-1">
        <ScopeChips scopes={token.scopes} globalAdmin={token.global_admin} />
      </div>

      <div className="hidden w-[150px] shrink-0 text-[12px] text-gray-500 desktop:block">
        {token.last_used_at ? (
          <>Used {formatDistanceToNow(new Date(token.last_used_at), {addSuffix: true})}</>
        ) : (
          <span className="text-gray-400">Never used</span>
        )}
        <div className="text-gray-400">Created {format(new Date(token.created_at), 'MMM d, yyyy')}</div>
        <Expiry at={token.expires_at} />
      </div>

      <div className="flex shrink-0 gap-1">
        <button onClick={onRotate} title="Rotate token" className={`${ICON_BUTTON} hover:text-[#654dcb]`}>
          <ArrowPathIcon />
        </button>
        <button onClick={onDelete} title="Delete token" className={`${ICON_BUTTON} hover:text-[#c2410c]`}>
          <TrashIcon />
        </button>
      </div>
    </div>
  </div>
);

export const McpTokensSection: FC = () => {
  const tokensQuery = useMcpTokens(AppState.currentProfile?.id);
  const tokens = tokensQuery.data || [];
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{token: IMcpToken; rotated: boolean} | null>(null);
  const [confirmingRotate, setConfirmingRotate] = useState<IMcpToken | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<IMcpToken | null>(null);

  const handleCreated = (token: IMcpToken) => {
    setCreating(false);
    setRevealed({token, rotated: false});
  };

  const handleRotate = () => {
    const target = confirmingRotate;
    setConfirmingRotate(null);
    if (!target) {
      return;
    }

    rotateMcpToken(target.id)
      .then((rotated) => setRevealed({token: rotated, rotated: true}))
      .catch((e: any) => toast.error(`Error rotating the MCP token: ${e.message}`));
  };

  const handleDelete = () => {
    const target = confirmingDelete;
    setConfirmingDelete(null);
    if (!target) {
      return;
    }

    deleteMcpToken(target.id)
      .then(() => toast.success('The MCP token has been deleted.'))
      .catch((e: any) => toast.error(`Error deleting the MCP token: ${e.message}`));
  };

  return (
    <>
      <div className="mb-3">
        <div className="text-[15px] font-medium text-[#33364b]">MCP tokens</div>
        <div className="mt-0.5 text-[13px] text-gray-500">
          MCP tokens connect AI assistants to your account. A token acts as you, with the scopes you choose, and can be revoked here at any
          time.
        </div>
      </div>

      {tokensQuery.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner mode="dark" />
        </div>
      ) : tokens.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#dcdee5] bg-[#fbfbfc] p-8 text-center">
          <div className="text-[15px] font-medium text-[#33364b]">No MCP tokens yet</div>
          <div className="mx-auto mt-1 max-w-[440px] text-[13px] text-gray-500">
            MCP tokens let an AI assistant like Claude or Cursor work with your templates and envelopes on your behalf. You will see the
            token once, when it is created.
          </div>
          <div className="mt-4 flex justify-center">
            <Button label="Create a token" onClick={() => setCreating(true)} />
          </div>
        </div>
      ) : (
        <Card>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-[15px] font-medium text-[#33364b]">
              {tokens.length} {tokens.length === 1 ? 'token' : 'tokens'}
            </div>
            <Button size="small" label="Create token" icon={<CommandLineIcon className="h-4 w-4" />} onClick={() => setCreating(true)} />
          </div>

          {tokens.map((token) => (
            <TokenRow
              key={token.id}
              token={token}
              onRotate={() => setConfirmingRotate(token)}
              onDelete={() => setConfirmingDelete(token)}
            />
          ))}
        </Card>
      )}

      {creating && <CreateMcpTokenDialog onCreated={handleCreated} onClose={() => setCreating(false)} />}

      {revealed && <CreatedMcpTokenDialog token={revealed.token} rotated={revealed.rotated} onClose={() => setRevealed(null)} />}

      {confirmingRotate && (
        <OkCancelDialog onOk={handleRotate} onCancel={() => setConfirmingRotate(null)}>
          <p className="text-[13px] text-gray-500">
            Rotating this token signs the assistant out immediately. You will get a new token to paste into its configuration. Continue?
          </p>
        </OkCancelDialog>
      )}

      {confirmingDelete && (
        <OkCancelDialog onOk={handleDelete} onCancel={() => setConfirmingDelete(null)}>
          <p className="text-[13px] text-gray-500">
            Deleting <span className="font-medium text-[#33364b]">{confirmingDelete.name}</span> disconnects anything using it immediately,
            and the token cannot be restored.
            {confirmingDelete.last_used_at
              ? ` This token was last used ${formatDistanceToNow(new Date(confirmingDelete.last_used_at), {addSuffix: true})}.`
              : ''}
          </p>
        </OkCancelDialog>
      )}
    </>
  );
};
